/**
 * Histórico completo de leituras do instrumento selecionado no M6 (GUPM-329).
 *
 * Só busca para tipo que declarou histórico (`temHistorico`) — hoje o marco
 * superficial. Sem filtro de canal: a linha do marco junta easting, northing e
 * vertical da mesma campanha.
 */
import { useQuery } from '@tanstack/react-query';
import { asInstrumentId, type Instrument } from '@/domain';
import {
  historicoDoInstrumento, temHistorico, type HistoricoDoInstrumento,
} from '@/domain/instrumentos/historico';
import { useDataSources } from '@/services/datasources';

/**
 * Teto de leituras por instrumento. Marco: 3 por campanha mensal ⇒ ~80 anos de
 * campanhas. Um teto e não "sem limite" porque a série automática, quando declarar
 * histórico, tem 144 leituras/dia — aquele tipo vai precisar de paginação própria.
 */
const LIMITE_HISTORICO = 3000;

export interface HistoricoInstrumentoData {
  historico: HistoricoDoInstrumento | null;
  isLoading: boolean;
  error: Error | null;
}

export function useHistoricoInstrumento(instrument: Instrument | null): HistoricoInstrumentoData {
  const { instrumentacao } = useDataSources();
  const ativo = !!instrument && temHistorico(instrument.type);

  const { data: leituras = [], isLoading, error } = useQuery({
    queryKey: ['readings', 'historico', instrument?.id ?? null],
    queryFn: () => instrumentacao.listLatestReadings({
      instrumentId: asInstrumentId(instrument!.id),
      limit: LIMITE_HISTORICO,
    }),
    enabled: ativo,
  });

  return {
    historico: ativo ? historicoDoInstrumento(instrument!, leituras) : null,
    isLoading: ativo && isLoading,
    error: error instanceof Error ? error : null,
  };
}
